import React, { useRef } from 'react'
import classNames from 'classnames'
import { LabelSM, TitleSM } from '@/atoms/Text'

export type PillTabItem = {
  id: string
  primary: string
  secondary?: string
}

export type PillTabsProps = {
  items: PillTabItem[]
  activeId?: string
  defaultActiveId?: string
  onChange?: (item: PillTabItem) => void
  className?: string
  tabClassName?: string
  ariaLabel?: string
}

export const PillTabs: React.FC<PillTabsProps> = ({
  items,
  activeId,
  defaultActiveId,
  onChange,
  className,
  tabClassName,
  ariaLabel,
}) => {
  const isControlled = typeof activeId !== 'undefined'

  const defaultIndex = React.useMemo(() => {
    if (defaultActiveId) {
      const idx = items.findIndex((it) => it.id === defaultActiveId)
      return idx >= 0 ? idx : 0
    }
    return 0
  }, [defaultActiveId, items])

  const [uncontrolledIndex, setUncontrolledIndex] = React.useState<number>(defaultIndex)

  React.useEffect(() => {
    if (!isControlled && uncontrolledIndex >= items.length) {
      setUncontrolledIndex(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  if (!items || items.length === 0) return null

  const selectedIndex = isControlled ? items.findIndex((it) => it.id === activeId) : uncontrolledIndex
  const safeSelectedIndex = selectedIndex >= 0 ? selectedIndex : 0

  const handleClick = (item: PillTabItem, index: number) => {
    if (!isControlled) setUncontrolledIndex(index)
    onChange?.(item)
  }

  /** WAI-ARIA Tabs keyboard navigation: Arrow keys move focus, Home/End jump to first/last tab. */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % items.length
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + items.length) % items.length
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = items.length - 1
        break
      default:
        return
    }

    event.preventDefault()
    const nextItem = items[nextIndex]
    if (!nextItem) return
    tabRefs.current[nextIndex]?.focus()
    handleClick(nextItem, nextIndex)
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={classNames(
        'gap-x-2 lg:gap-x-0 mb-6 flex flex-row px-4 sm:px-8 md:px-12 lg:mb-0 scrollbar-none lg:flex-col lg:gap-y-[14px] lg:pb-6 xl:px-16 overflow-x-auto touch-pan-x snap-x snap-mandatory lg:snap-none lg:overflow-x-visible',
        className,
      )}
    >
      {items.map((item, index) => {
        const selected = index === safeSelectedIndex
        return (
          <button
            key={item.id}
            ref={(el) => {
              tabRefs.current[index] = el
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => handleClick(item, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={classNames(
              'group-pill-tab lg:min-w-[300px] flex flex-row focus-visible:outline-none focus-visible:ring-0 focus-visible:right-offset-0 focus-visible:ring-transparent lg:justify-between py-2 px-4 lg:py-[17px] lg:px-[27px] rounded-full w-fit cursor-pointer transition-colors duration-300 ease-in-out focus:outline-none',
              selected
                ? 'bg-white text-core-gray-950 lg:text-white lg:bg-[#434343] lg:hover:bg-[#555555]'
                : 'text-white lg:hover:bg-oxide-primary lg:hover:text-core-gray-950',
              tabClassName,
            )}
          >
            <LabelSM weight="semibold" className="lg:photon-title-sm w-fit select-none whitespace-nowrap">
              {item.primary}
            </LabelSM>
            {item.secondary ? (
              <TitleSM
                className={classNames('hidden select-none lg:flex transition-all ease-in-out duration-200', {
                  'group-pill-tab-hover:text-core-gray-950 text-core-gray-500': true,
                  'text-white': false,
                })}
              >
                {item.secondary}
              </TitleSM>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
